import { useEffect, useContext, useRef } from 'react';
import styled from '@emotion/styled';
import { Sidebar } from 'semantic-ui-react';
import aspectRatio from 'calculate-aspect-ratio';
import { useEyeDrop } from 'react-eyedrop';
import html2canvas from 'html2canvas';
import { format } from 'date-fns';
import { store } from '../store';
import Nav from './Nav';
import Settings from './Settings';
import Paper from './Paper';
import ColorCard from './ColorCard';
import DownloadToast from './DownloadToast';
import LoadDataModal from './LoadDataModal';
import {
  SET_IMAGE_DOWNLOADED,
  SET_IMAGE_DOWNLOADING,
  SET_PICKED_COLOR,
  SET_PICKING_COLOR,
  SET_SHOW_LOAD_MODAL,
  UPDATE_PAPER_HEIGHT_PX,
  UPDATE_PAPER_LAYOUT,
  UPDATE_PAPER_WIDTH_PX,
} from '../types';
import useWindowSize from '../hooks/useWindowSize';
import { getLayout, getSuggestedDimension, saveFile } from '../utils';
import ColorPalette from './ColorPalette';

function App() {
  const {
    paperWidth,
    paperHeight,
    pickingColor,
    pickedColor,
    grid,
    gridSize,
    border,
    showColorPalette,
    dispatch,
  } = useContext(store);
  const paperRef = useRef();
  const [colors, pickColor, cancelColorPick] = useEyeDrop({
    once: true,
    cursorActive: 'default',
  });
  const { width, height } = useWindowSize();

  const setPickingColor = (picking) =>
    dispatch({ type: SET_PICKING_COLOR, payload: picking });

  const handleColorPick = () => {
    setPickingColor(true);
    pickColor();
  };

  const cancelColorPicker = (event) => {
    event.stopPropagation();

    setPickingColor(false);
    cancelColorPick();
  };

  useEffect(() => {
    const appData = JSON.parse(localStorage.getItem('photoReference'));
    if (appData) dispatch({ type: SET_SHOW_LOAD_MODAL, payload: true });
  }, []);

  useEffect(() => {
    const { hex } = colors;
    dispatch({ type: SET_PICKED_COLOR, payload: hex });
  }, [colors]);

  const handleClick = (event) => {
    if (pickingColor) {
      if (event.target.id !== 'photo') cancelColorPicker(event);
    }
  };

  useEffect(() => {
    const removeClickListener = () =>
      document.removeEventListener('click', handleClick, false);

    if (pickingColor) document.addEventListener('click', handleClick, false);
    else removeClickListener();

    return removeClickListener;
  }, [pickingColor]);

  useEffect(() => {
    if (width && height) {
      const ratio = aspectRatio(paperWidth, paperHeight);
      const layout = getLayout(ratio);
      const widthConstraint = width > 1000 ? width - 100 : width - 40;
      const {
        width: suggestedWidth,
        height: suggestedHeight,
      } = getSuggestedDimension(
        { width: paperWidth, height: paperHeight },
        {
          width: layout === 'landscape' ? widthConstraint : undefined,
          height: layout !== 'landscape' ? height - 100 : undefined,
        }
      );
      dispatch({ type: UPDATE_PAPER_LAYOUT, payload: layout });
      dispatch({ type: UPDATE_PAPER_WIDTH_PX, payload: suggestedWidth });
      dispatch({ type: UPDATE_PAPER_HEIGHT_PX, payload: suggestedHeight });
    }
  }, [paperWidth, paperHeight, width, height]);

  const handleImageExport = async () => {
    const { current: el } = paperRef;
    if (el) {
      dispatch({ type: SET_IMAGE_DOWNLOADING, payload: true });
      const canvas = await html2canvas(el, {
        allowTaint: true,
        useCORS: true,
        scale: 4,
      });
      var image = canvas.toDataURL('image/png', 1.0);
      const date = format(new Date(), 'M-d-yy');
      const size = `${paperWidth}x${paperHeight}`;
      const borderSize = border ? `border${border}` : '';
      const gridDisplay = grid ? `grid${gridSize}` : '';
      const fileName = `${size}${borderSize}${gridDisplay}_${date}.png`;
      await saveFile(image, fileName);
      dispatch({ type: SET_IMAGE_DOWNLOADING, payload: false });
      dispatch({ type: SET_IMAGE_DOWNLOADED, payload: true });
      setTimeout(() => {
        dispatch({ type: SET_IMAGE_DOWNLOADED, payload: false });
      }, 3000);
    }
  };

  return (
    <Container>
      <Nav
        onColorPick={handleColorPick}
        cancelColorPicker={cancelColorPicker}
        exportImage={handleImageExport}
      />
      {pickedColor && <ColorCard />}
      <ToastContainer>
        <DownloadToast />
      </ToastContainer>
      <Sidebar.Pushable>
        <Settings />
        <Sidebar.Pusher>
          {showColorPalette ? (
            <ColorPalette />
          ) : (
            <PaperContainer>
              <Paper paperRef={paperRef} />
            </PaperContainer>
          )}
        </Sidebar.Pusher>
      </Sidebar.Pushable>
      <LoadDataModal />
    </Container>
  );
}

const Container = styled.div({
  display: 'grid',
  gridTemplateRows: 'auto 1fr',
  backgroundColor: '#EEEEFF',
  height: '100vh',
  position: 'relative',
});

const PaperContainer = styled.div({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const ToastContainer = styled.div({
  position: 'absolute',
  top: 70,
  right: 5,
  zIndex: 1,
});

export default App;
